import { create } from "zustand"

import type { AdjustmentHistorySearchParams } from "../schemas"

import {
  type AdjustmentHistoryItem,
  GetInventoryAdjustmentHistoryListDocument,
  type GetInventoryAdjustmentHistoryListQuery,
} from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

const deduplicateAdjustmentHistoryItems = (
  newData: AdjustmentHistoryItem[],
  existingData: AdjustmentHistoryItem[]
): AdjustmentHistoryItem[] => {
  const uniqueIds = new Set(existingData.map((item) => item.inventoryAdjustmentId))
  return [...existingData, ...newData.filter((item) => !uniqueIds.has(item.inventoryAdjustmentId))]
}

export const useAdjustmentHistorySearchStore = create(
  createSearchStore<AdjustmentHistoryItem, AdjustmentHistorySearchParams, GetInventoryAdjustmentHistoryListQuery>({
    documentNode: GetInventoryAdjustmentHistoryListDocument,
    deduplicateData: deduplicateAdjustmentHistoryItems,
    getQueryInput: (filters: AdjustmentHistorySearchParams, offset: number, limit: number) => {
      const {
        field,
        order,
        query,
        adjustmentTypes,
        adjustmentReasons,
        statuses,
        warehouseAreaIds,
        adjustedByIds,
        dateFrom,
        dateTo,
      } = filters
      return {
        offset,
        limit,
        search: query,
        sort: field && order ? { field, order } : undefined,
        filter: {
          adjustmentTypes: adjustmentTypes?.length ? adjustmentTypes : undefined,
          adjustmentReasons: adjustmentReasons?.length ? adjustmentReasons : undefined,
          statuses: statuses?.length ? statuses : undefined,
          warehouseAreaIds: warehouseAreaIds?.length ? warehouseAreaIds : undefined,
          adjustedByIds: adjustedByIds?.length ? adjustedByIds : undefined,
          dateRange: dateFrom ? { from: dateFrom, to: dateTo ?? undefined } : undefined,
        },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.inventoryAdjustment.list.__typename,
      data:
        data.inventoryAdjustment.list.__typename === "AdjustmentHistoryListSuccess"
          ? (data.inventoryAdjustment.list.items as AdjustmentHistoryItem[])
          : [],
      error:
        data.inventoryAdjustment.list.__typename === "AdjustmentHistoryListFailure"
          ? data.inventoryAdjustment.list.error
          : undefined,
    }),
    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
